import { Box, FormControl, FormHelperText, InputBase, InputLabel, useMediaQuery, useTheme } from '@mui/material';
import { Controller, Control, get } from 'react-hook-form';
import { HTMLInputTypeAttribute, useId } from 'react';

type DualFieldName = [string, string];
type DualFieldType = [HTMLInputTypeAttribute, HTMLInputTypeAttribute];
type DualPlaceholder = [string, string];

interface Props {
  name: DualFieldName;
  type?: DualFieldType;
  placeholder?: DualPlaceholder;
  label: string;
  control: Control<any>;
  required?: boolean;
  fullWidth?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
}

export default function DualInput({
  name: [leftName, rightName],
  type = ['text', 'text'],
  placeholder = ['', ''],
  label,
  control,
  required = false,
  fullWidth = true,
  helperText = null,
  disabled = false
}: Props) {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const labelId = useId();
  const [leftType, rightType] = type;
  const [leftPlaceholder, rightPlaceholder] = placeholder;

  return (
    <Controller
      name={leftName}
      control={control}
      render={({ field: leftField, formState: { errors } }) => (
        <Controller
          name={rightName}
          control={control}
          render={({ field: rightField }) => {
            const leftError = get(errors, leftName);
            const rightError = get(errors, rightName);
            const errorMessage = leftError?.message || rightError?.message;
            const hasError = Boolean(leftError || rightError);

            return (
              <FormControl
                variant='outlined'
                fullWidth={fullWidth}
                error={hasError}
                disabled={disabled}
                required={required}
                sx={{
                  '& .MuiFormLabel-asterisk': {
                    color: 'red'
                  }
                }}
              >
                <InputLabel
                  id={labelId}
                  shrink
                  htmlFor={leftField.name}
                  sx={{
                    bgcolor: '#fff',
                    px: 0.5,
                    transform: 'translate(14px, -9px) scale(0.75)'
                  }}
                >
                  {label}
                </InputLabel>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    border: '1px solid',
                    borderColor: hasError ? 'error.main' : '#ececec',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    minHeight: isLargeScreen ? 56 : 40,
                    '&:hover': {
                      borderColor: hasError ? 'error.main' : 'rgba(0, 0, 0, 0.87)'
                    },
                    '&:focus-within': {
                      borderColor: hasError ? 'error.main' : theme.palette.primary.main,
                      borderWidth: 2,
                      margin: '-1px'
                    }
                  }}
                >
                  <InputBase
                    {...leftField}
                    id={leftField.name}
                    type={leftType}
                    placeholder={leftPlaceholder}
                    disabled={disabled}
                    autoComplete='off'
                    sx={{
                      flex: '1 1 55%',
                      alignSelf: 'stretch',
                      px: 1.75,
                      fontSize: isLargeScreen ? 16 : 14,
                      borderRadius: 0,
                      '& input': {
                        p: 0,
                        height: '100%'
                      }
                    }}
                  />

                  <Box
                    sx={{
                      width: '1px',
                      alignSelf: 'stretch',
                      my: '10px',
                      bgcolor: '#ececec',
                      flexShrink: 0
                    }}
                  />

                  <InputBase
                    {...rightField}
                    type={rightType}
                    placeholder={rightPlaceholder}
                    disabled={disabled}
                    autoComplete='off'
                    sx={{
                      flex: '1 1 35%',
                      alignSelf: 'stretch',
                      px: 1.75,
                      fontSize: isLargeScreen ? 16 : 14,
                      borderRadius: 0,
                      '& input': {
                        p: 0,
                        height: '100%'
                      }
                    }}
                  />
                </Box>

                {(errorMessage || helperText) && (
                  <FormHelperText error={hasError}>{errorMessage || helperText}</FormHelperText>
                )}
              </FormControl>
            );
          }}
        />
      )}
    />
  );
}
